/**
 * Web i18n runtime (#799, docs/I18N.md). Strings come from the shared catalog (shared/i18n), generated into
 * ./locales/<code>.json by `npm run i18n`; ./keys.ts types every key and its arguments.
 *
 * Why not react-intl / FormatJS: the catalog is already restricted to a small ICU subset (one plural or select per
 * message, integer arguments) that the generators also emit for Android and iOS, so the ~150-line formatter in
 * icu.ts covers it exactly, with no runtime dependency (react-intl + intl-messageformat would add ~15-20 kB gzip
 * to the main chunk). Plural rules and number/date formatting come from the browser's Intl.
 *
 * - English (en.json) is bundled and is the per-key fallback; every other locale is a lazy chunk, loaded before
 *   it becomes active (initI18n before the first render, setLocaleChoice afterwards).
 * - The active locale is a module singleton, so plain modules (toasts, error text) can call t() too. React
 *   components call useT() (./useT.ts), which re-renders them when the language changes.
 * - Choice: ?lang=<code> (testing; also en-XA) > the remembered choice (localStorage, per device) > the browser's
 *   languages, negotiated against LOCALES > English. Language never affects geofencing or any behaviour.
 */
import en from './locales/en.json';
import { LOCALES, SOURCE_LOCALE, type LocaleCode, type MessageArgs, type MessageKey } from './keys';
import { format as formatIcu, parse, type ArgValue, type Message } from './icu';

export type { LocaleCode, MessageKey, MessageArgs };

/** 'system' = follow the browser's languages. */
export type LocaleChoice = LocaleCode | 'system';

export const LANG_STORAGE_KEY = 'hip4.lang';
export const LANG_PARAM = 'lang';
const PSEUDO: LocaleCode = 'en-XA';

const CODES = new Set<string>(LOCALES.map((l) => l.code));
export function isLocaleCode(v: unknown): v is LocaleCode {
  return typeof v === 'string' && CODES.has(v);
}

type Bundle = Record<string, string>;
const EN: Bundle = en;

// Every generated locale file except en (bundled) becomes its own chunk. Locales with no strings yet have no file
// and render English.
const loaders = import.meta.glob<{ default: Bundle }>(['./locales/*.json', '!./locales/en.json']);
const loaderFor = (code: LocaleCode) => loaders[`./locales/${code}.json`];

const bundles = new Map<LocaleCode, Bundle>([[SOURCE_LOCALE as LocaleCode, EN]]);

// ------------------------------------------------------------------------------------------- negotiation

/**
 * Browser language list → our locale (docs/I18N.md "Locale choice"): exact match, then same language + script
 * (zh-CN/zh-SG → zh-Hans), then same language in any region (es-MX/es-ES → es-419, pt-PT → pt-BR), else English.
 * Every Chinese tag, Traditional included (zh-Hant, zh-TW, zh-HK, zh-MO), gets Simplified Chinese by owner decision
 * (#869). The legacy codes in (Indonesian) and iw (Hebrew) count as id and he (#900). en-XA is never negotiated.
 */
// Legacy ISO 639 codes that old Java/Android locales and some WebViews still send (#900).
const LEGACY_LANG: Record<string, string> = { in: 'id', iw: 'he' };

export function negotiate(langs: readonly string[] | undefined | null): LocaleCode {
  const real = LOCALES.filter((l) => !l.pseudo).map((l) => l.code as LocaleCode);
  for (const raw of langs ?? []) {
    if (typeof raw !== 'string' || !raw) continue;
    const parts = raw.replace(/_/g, '-').toLowerCase().split('-');
    parts[0] = LEGACY_LANG[parts[0]!] ?? parts[0]!;
    const lower = parts.join('-');
    const exact = real.find((c) => c.toLowerCase() === lower);
    if (exact) return exact;
    const [lang] = lower.split('-');
    if (lang === 'zh') return 'zh-Hans';
    const sameLang = real.find((c) => c.toLowerCase().split('-')[0] === lang);
    if (sameLang) return sameLang;
  }
  return SOURCE_LOCALE as LocaleCode;
}

function browserLanguages(): string[] {
  if (typeof navigator === 'undefined') return [];
  const list = navigator.languages?.length ? navigator.languages : navigator.language ? [navigator.language] : [];
  return [...list];
}

function storage(): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

export function loadStoredChoice(): LocaleChoice {
  try {
    const v = storage()?.getItem(LANG_STORAGE_KEY);
    return isLocaleCode(v) ? v : 'system';
  } catch {
    return 'system';
  }
}

function storeChoice(choice: LocaleChoice) {
  try {
    const s = storage();
    if (!s) return;
    if (choice === 'system') s.removeItem(LANG_STORAGE_KEY);
    else s.setItem(LANG_STORAGE_KEY, choice);
  } catch {
    /* storage blocked: the choice lasts for this page view */
  }
}

function urlLocale(): LocaleCode | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = new URLSearchParams(window.location.search).get(LANG_PARAM);
    return isLocaleCode(v) ? v : null;
  } catch {
    return null;
  }
}

/** The locale a choice resolves to right now. */
export function resolveChoice(choice: LocaleChoice): LocaleCode {
  return choice === 'system' ? negotiate(browserLanguages()) : choice;
}

// ------------------------------------------------------------------------------------------- state

interface State {
  locale: LocaleCode;
  choice: LocaleChoice;
  /** Bumps on every change; React subscribes to it. */
  version: number;
}

let state: State = { locale: SOURCE_LOCALE as LocaleCode, choice: 'system', version: 0 };
const listeners = new Set<() => void>();

export function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getState(): State {
  return state;
}

export function getLocale(): LocaleCode {
  return state.locale;
}

/**
 * BCP 47 tag for Intl (the pseudo-locale formats like English; en keeps the en-US formats used before #799). Every
 * locale formats with Western (Latin) digits: Arabic gets `-u-nu-latn`, so even an Intl call that forgets
 * `numberingSystem: 'latn'` cannot switch prices to Arabic-Indic digits (#900, docs/I18N.md "Right-to-left").
 */
export function intlLocale(code: LocaleCode = state.locale): string {
  if (code === PSEUDO || code === 'en') return 'en-US';
  if (code === 'ar') return 'ar-u-nu-latn';
  return code;
}

const DIRS = new Map<string, 'ltr' | 'rtl'>(LOCALES.map((l) => [l.code, l.dir]));

/** Writing direction of a locale (locales.json "rtl"): 'rtl' for Arabic and Hebrew. */
export function localeDir(code: LocaleCode = state.locale): 'ltr' | 'rtl' {
  return DIRS.get(code) ?? 'ltr';
}

/**
 * Upper-case UI text with the active language's rules (Turkish i → İ, not I). For catalog text only: identifiers,
 * tickers and wire values keep plain toUpperCase(). CSS `uppercase` already follows `<html lang>`.
 */
export function upperUi(s: string, code: LocaleCode = state.locale): string {
  return s.toLocaleUpperCase(intlLocale(code));
}

/** Lower-case UI text with the active language's rules (Turkish I → ı). */
export function lowerUi(s: string, code: LocaleCode = state.locale): string {
  return s.toLocaleLowerCase(intlLocale(code));
}

async function ensureBundle(code: LocaleCode): Promise<void> {
  if (bundles.has(code)) return;
  const load = loaderFor(code);
  if (!load) {
    bundles.set(code, {}); // nothing translated yet: English for every key
    return;
  }
  try {
    bundles.set(code, (await load()).default);
  } catch {
    bundles.set(code, {}); // chunk failed to load (offline): English rather than a broken page
  }
}

function apply(locale: LocaleCode, choice: LocaleChoice) {
  state = { locale, choice, version: state.version + 1 };
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
    // Arabic and Hebrew mirror the whole layout; the CSS uses logical properties (ms-/pe-/end-/text-end) so it
    // follows. Numbers, the order book and the trades tape opt back into LTR (index.css `.num`, `dir="ltr"`).
    document.documentElement.dir = localeDir(locale);
  }
  listeners.forEach((l) => l());
}

/** Load the locale for `choice`, remember it on this device, and switch. */
export async function setLocaleChoice(choice: LocaleChoice): Promise<void> {
  const locale = resolveChoice(choice);
  await ensureBundle(locale);
  storeChoice(choice);
  apply(locale, choice);
}

/**
 * Pick and load the starting locale before the first render (main.tsx), so a non-English user never sees an
 * English flash. ?lang= wins for this page view without being remembered.
 */
export async function initI18n(): Promise<void> {
  const fromUrl = urlLocale();
  const choice = loadStoredChoice();
  const locale = fromUrl ?? resolveChoice(choice);
  await ensureBundle(locale);
  apply(locale, choice);
}

/** Test helper: switch synchronously to an already-loaded (or English-only) locale. */
export function setLocaleForTest(locale: LocaleCode, bundle?: Bundle) {
  if (bundle) bundles.set(locale, bundle);
  else if (!bundles.has(locale)) bundles.set(locale, {});
  apply(locale, locale);
}

// ------------------------------------------------------------------------------------------- formatting

const compiled = new Map<string, Message>();
function compile(src: string): Message {
  let m = compiled.get(src);
  if (!m) {
    m = parse(src);
    compiled.set(src, m);
  }
  return m;
}

const pluralRules = new Map<string, Intl.PluralRules>();
const intFormats = new Map<string, Intl.NumberFormat>();

function env(locale: LocaleCode) {
  const tag = intlLocale(locale);
  let pr = pluralRules.get(tag);
  if (!pr) pluralRules.set(tag, (pr = new Intl.PluralRules(tag)));
  let nf = intFormats.get(tag);
  if (!nf) intFormats.set(tag, (nf = new Intl.NumberFormat(tag, { maximumFractionDigits: 0, numberingSystem: 'latn' } as Intl.NumberFormatOptions)));
  const rules = pr;
  const ints = nf;
  // RTL: every interpolated value is a bidi isolate, so "Buy {side} {title} at {px}" keeps a Latin title and a price
  // in place in Arabic/Hebrew (#900). LTR output is unchanged.
  return { plural: (n: number) => rules.select(n), number: (n: number) => ints.format(n), isolate: localeDir(locale) === 'rtl' };
}

/** The ICU source for a key in a locale, falling back to English, then to the key itself. */
export function messageSource(key: string, locale: LocaleCode = state.locale): string {
  return bundles.get(locale)?.[key] ?? EN[key] ?? key;
}

/** Keys whose message takes no arguments (for lookup tables: `Record<SortKey, PlainKey>`). */
export type PlainKey = { [K in MessageKey]: MessageArgs[K] extends Record<string, never> ? K : never }[MessageKey];

type ArgsParam<K extends MessageKey> = MessageArgs[K] extends Record<string, never> ? [] : [MessageArgs[K]];

/** Translate `key` in the active locale. Arguments follow the English message (keys.ts types them). */
export function t<K extends MessageKey>(key: K, ...args: ArgsParam<K>): string {
  return formatIcu(compile(messageSource(key)), args[0] as Record<string, ArgValue> | undefined, env(state.locale)).join('');
}

/** Rich-text arguments: any string argument may be a React node (a link, <strong>…) instead of a string. */
export type RichArgs<K extends MessageKey> = { [P in keyof MessageArgs[K]]: MessageArgs[K][P] | object };

/** Like t(), but string arguments may be React nodes; returns the pieces to render as children. */
export function tPieces<K extends MessageKey>(key: K, args: RichArgs<K>): unknown[] {
  const out = formatIcu(compile(messageSource(key)), args as Record<string, ArgValue>, env(state.locale));
  // Merge adjacent strings so React gets few text nodes.
  const merged: unknown[] = [];
  for (const p of out) {
    const last = merged.length - 1;
    if (typeof p === 'string' && typeof merged[last] === 'string') merged[last] = (merged[last] as string) + p;
    else merged.push(p);
  }
  return merged;
}

/** Untyped lookup for keys built at runtime (`catalog.sort.${k}`); unknown keys return the key. */
export function tDynamic(key: string, args?: Record<string, ArgValue>): string {
  return formatIcu(compile(messageSource(key)), args, env(state.locale)).join('');
}

export function hasKey(key: string): boolean {
  return key in EN;
}
